import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { FiUser, FiMail, FiLock, FiCalendar, FiArrowRight } from '../utils/icons';
import { useLanguage } from '../utils/languageContext';
import { useTheme } from '../utils/ThemeContext';

const Profile = () => {
  const { translations } = useLanguage();
  
  // Mock user data
  const [profile, setProfile] = useState({
    name: 'Bill kong',
    email: 'bill.kong@example.com',
    phone: '+1 (555) 123-4567',
    dob: '1985-04-15',
    address: '123 Main Street, San Francisco, CA 94105',
    securityQuestions: {
      question1: "What was your first pet's name?",
      answer1: '•••••••••',
      question2: 'In which city were you born?',
      answer2: '•••••••••'
    }
  });
  
  const [isEditing, setIsEditing] = useState(false);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsEditing(false);
    // In a real app, you'd send this data to the server
    console.log('Profile updated:', profile);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-text mb-2">{translations.personalInformation}</h1>
          <p className="text-text text-opacity-70">
            View and update your personal information.
          </p>
        </div>
        
        <div className="bg-foreground rounded-xl p-6 shadow-sm border border-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Profile Details</h2>
            <button 
              onClick={() => setIsEditing(!isEditing)}
              className="px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-text text-opacity-70 mb-1">Full Name</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text text-opacity-50">
                    <FiUser size={18} />
                  </span>
                  <input
                    type="text"
                    name="name"
                    value={profile.name}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full py-2 pl-10 pr-3 rounded-lg bg-card border border-border focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-75 disabled:bg-card-hover"
                  />
                </div>
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-text text-opacity-70 mb-1">Email Address</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text text-opacity-50">
                    <FiMail size={18} />
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={profile.email}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full py-2 pl-10 pr-3 rounded-lg bg-card border border-border focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-75 disabled:bg-card-hover"
                  />
                </div>
              </div>
              
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-text text-opacity-70 mb-1">Phone Number</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text text-opacity-50">
                    <FiUser size={18} />
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    value={profile.phone}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full py-2 pl-10 pr-3 rounded-lg bg-card border border-border focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-75 disabled:bg-card-hover"
                  />
                </div>
              </div>
              
              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium text-text/70 mb-1">Date of Birth</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text/50">
                    <FiCalendar size={18} />
                  </span>
                  <input
                    type="date"
                    name="dob"
                    value={profile.dob}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full py-2 pl-10 pr-3 rounded-lg bg-card border border-border focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-75 disabled:bg-card-hover"
                  />
                </div>
              </div>
              
              {/* Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text/70 mb-1">Address</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text/50">
                    <FiUser size={18} />
                  </span>
                  <input
                    type="text"
                    name="address"
                    value={profile.address}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full py-2 pl-10 pr-3 rounded-lg bg-card border border-border focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-75 disabled:bg-card-hover"
                  />
                </div>
              </div>
            </div>
            
            {isEditing && (
              <div className="mt-8 flex justify-end">
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center"
                >
                  <FiArrowRight className="mr-2" size={18} />
                  {translations.save}
                </button>
              </div>
            )}
          </form>
          
          <div className="mt-10 pt-6 border-t border-border">
            <h3 className="text-lg font-semibold mb-4">Security Questions</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-1">{profile.securityQuestions.question1}</p>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text/50">
                    <FiLock size={18} />
                  </span>
                  <input
                    type="password"
                    value={profile.securityQuestions.answer1}
                    disabled
                    className="w-full md:w-1/2 py-2 pl-10 pr-3 rounded-lg bg-card-hover border border-border disabled:opacity-75"
                  />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-1">{profile.securityQuestions.question2}</p>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text/50">
                    <FiLock size={18} />
                  </span>
                  <input
                    type="password"
                    value={profile.securityQuestions.answer2}
                    disabled
                    className="w-full md:w-1/2 py-2 pl-10 pr-3 rounded-lg bg-card-hover border border-border disabled:opacity-75"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile; 